import * as z from 'zod';
import type { ZodError } from 'zod';

import { CampaignCheckoutCmsContent } from './campaign-checkout-content.ts';

export enum CartItemTypeCodeEnum {
  CDSP = 'CDSP',
  Fund = 'Fund',
  AnyProduct = 'AnyProduct',
  Anything = 'Anything',
}

/** Legacy value for AnyProduct stored in older DynamoDB records. */
export const LEGACY_ANY_PRODUCT_VALUE = 'Any Product';
// const CartItemTypeCodeZodEnum = z.enum(CartItemTypeCodeEnum);
// export type CartItemTypeCodeZodEnum = z.infer<typeof CartItemTypeCodeZodEnum>;

export const PremiumTier = z.object({
  acceptanceText: z.string().optional().nullable(),
  confirmationText: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  endDate: z.string().optional().nullable(),
  imagePathCheckout: z.string().optional().nullable(),
  imagePathReview: z.string().optional().nullable(),
  minimumDonationAmount: z.number(),
  requiresRecurringPaymentPlan: z.boolean().optional().nullable(),
  sourceCode: z.string().optional().nullable(),
  startDate: z.string().optional().nullable(),
  title: z.string().optional().nullable(),
});
export type PremiumTier = z.infer<typeof PremiumTier>;

const cartItemTypeLowercase = Object.values(CartItemTypeCodeEnum).map((v) =>
  v.toLowerCase(),
) as [string, ...string[]];
export const SuccessCriteria = z.object({
  criterionCartItemType: z
    .union([
      z.enum(CartItemTypeCodeEnum),
      z.enum(cartItemTypeLowercase),
      z.literal(LEGACY_ANY_PRODUCT_VALUE), // allows for backward compatibility
    ])
    .optional()
    .nullable(),
  criterionCDSPCountry: z.string().optional().nullable(),
  criterionCDSPProject: z.string().optional().nullable(),
  criterionCDSPHiv: z.boolean().optional().nullable(),
  criterionFund: z.string().optional().nullable(),
  premiumTiers: z.array(PremiumTier).optional().nullable(),
});
export type SuccessCriteria = z.infer<typeof SuccessCriteria>;

export const FundOverride = z.object({
  cmsId: z.string(),
  title: z.string(),
});
export type FundOverride = z.infer<typeof FundOverride>;

export const AddonOverride = FundOverride.extend({
  unitPrice: z.number().nonnegative(),
});
export type AddonOverride = z.infer<typeof AddonOverride>;

export const CheckoutCmsItemDetails = z.object({
  cmsContentId: z.string().optional().nullable(),
  checkoutCmsContentTitle: z.string().optional().nullable(),
});
export type CheckoutCmsItemDetails = z.infer<typeof CheckoutCmsItemDetails>;

export enum UpsellTypeEnum {
  CDSP = 'CDSP',
  Fund = 'Fund',
}
const UpsellOverrideBase = z.object({
  criterionCartItemType: z
    .union([
      z.enum(CartItemTypeCodeEnum),
      z.enum(cartItemTypeLowercase),
      z.literal(LEGACY_ANY_PRODUCT_VALUE), // allows for backward compatibility
    ])
    .optional()
    .nullable(),
  title: z.string().optional().nullable(),
  subtitle: z.string().optional().nullable(),
});

const UpsellOverrideCDSP = UpsellOverrideBase.extend({
  type: z.literal(UpsellTypeEnum.CDSP),
  sacFilter: z.string().optional().nullable(),
  sacFilterContentfulEntryId: z.string().optional().nullable(),
  sacFilterContentfulEntryTitle: z.string().optional().nullable(),
  sacFilterTarget: z
    .enum(['internal', 'local'])
    .default('internal')
    .describe('Use Contentful SAC Filter.'),
});

const UpsellOverrideFund = UpsellOverrideBase.extend({
  type: z.literal(UpsellTypeEnum.Fund),
  fund: FundOverride.extend({
    cmsId: z.string().min(1),
    icon: z.string().optional().nullable(),
    title: z.string().min(1),
    unitPrice: z.number().positive(),
  }),
});

export const UpsellOverride = z.discriminatedUnion('type', [
  UpsellOverrideCDSP,
  UpsellOverrideFund,
]);
export type UpsellOverride = z.infer<typeof UpsellOverride>;

export const FundAppeal = z.object({
  askValues: z.string().describe('Comma-delimited list of donation amounts.'),
  defaultSelected: z
    .number()
    .optional()
    .nullable()
    .describe(
      'Default selected value. If blank or value not found, second value will be selected by default.',
    ),
  allowOtherInput: z
    .boolean()
    .describe('Allow user to enter custom donation amount.'),
});
export type FundAppeal = z.infer<typeof FundAppeal>;

export const FundAppeals = z.object({
  oneTime: FundAppeal.optional().nullable(),
  monthly: FundAppeal.optional().nullable(),
});
export type FundAppeals = z.infer<typeof FundAppeals>;

export const SortColumnEnum = z
  .enum([
    'campaignName',
    'createdDate',
    'referrerCode',
    'sourceCode',
    'vanityUrl',
  ])
  .describe('Which column is the sort column on?');

export const DynamoDBPrimaryKey = z.object({
  PK: z.string().max(2048),
  SK: z.string().max(1024),
});
export type DynamoDBPrimaryKey = z.infer<typeof DynamoDBPrimaryKey>;

export const DynamoDBSecondaryKey = z.object({
  GSI1PK: z.string().max(2048),
  GSI1SK: z.string().max(1024),
  GSI2PK: z.string().max(2048),
  GSI2SK: z.string().max(1024),
  GSI3PK: z.string().max(2048),
  GSI3SK: z.string().max(1024),
  GSI4PK: z.string().max(2048),
  GSI4SK: z.string().max(1024),
  GSI5PK: z.string().max(2048).optional(),
  GSI5SK: z.string().max(1024).optional(),
  GSI6PK: z.string().max(2048).optional(),
  GSI6SK: z.string().max(1024).optional(),
  GSI7PK: z.string().max(2048).optional(),
  GSI7SK: z.string().max(1024).optional(),
  GSI8PK: z.string().max(2048).optional(),
  GSI8SK: z.string().max(1024).optional(),
  GSI9PK: z.string().max(2048).optional(),
  GSI9SK: z.string().max(1024).optional(),
  GSI10PK: z.string().max(2048).optional(),
  GSI10SK: z.string().max(1024).optional(),
  GSI11PK: z.string().max(2048).optional(),
  GSI11SK: z.string().max(1024).optional(),
});
export type DynamoDBSecondaryKey = z.infer<typeof DynamoDBSecondaryKey>;

export enum CampaignTypeEnum {
  Other = 'Other',
  SponsorAChild = 'SponsorAChild',
}

export const CampaignPartner = z.object({
  enabled: z.boolean().optional().nullable(),
  slug: z
    .string()
    .max(100)
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      'Slug must be lowercase alphanumeric with hyphens (e.g. "partner-name")',
    )
    .optional()
    .nullable(),
  name: z.string().optional().nullable(),
  logo: z.string().optional().nullable(),
  backgroundImage: z.string().optional().nullable(),
  sacFilter: z.string().optional().nullable(),
  sacFilterContentfulEntryId: z.string().optional().nullable(),
  sacFilterContentfulEntryTitle: z.string().optional().nullable(),
  sacFilterTarget: z.enum(['internal', 'local']).optional().nullable(),
  bodyContentRef: z.string().optional().nullable(),
});
export type CampaignPartner = z.infer<typeof CampaignPartner>;

/**
 * Campaign
 * @description The campaign object
 * @notes This is NOT a strict object and therefore will strip out any unknown properties when parsing.
 */
export const Campaign = z.object({
  id: z.string(),
  campaignName: z.string().optional().nullable(),
  campaignDisplayName: z.string().optional().nullable(),
  // TODO :: once we don't have to support Tridion any more, these should be required
  campaignOwner: z.string().nullish(),
  campaignType: z.enum(CampaignTypeEnum).optional(),
  cdspOverrideRate: z.number().optional().nullable(),
  cdspOverrideRateDuration: z.number().optional().nullable(),
  checkoutRedirect: z.string().optional().nullable(),
  showPrivacyOption: z.boolean().optional().nullable(),
  checkoutUrl: z.string().optional().nullable(),
  checkoutCmsItemDetails: CheckoutCmsItemDetails.optional().nullable(),
  /**
   * Resolved campaign checkout content from Contentful.
   * When the SDG API resolves the `checkoutCmsItemDetails.cmsContentId`,
   * it populates this field with the full rendered payload.
   */
  checkoutCmsContent: CampaignCheckoutCmsContent.optional().nullable(),
  createdDate: z.string().optional().nullable(),
  expirationDate: z.string().optional().nullable(),
  fundAppeals: FundAppeals.optional().nullable(),
  legacyId: z.string().optional().nullable(),
  offerDualRate: z.boolean().default(false),
  partner: CampaignPartner.optional().nullable(),
  // TODO :: once we don't have to support Tridion any more, these should be required
  published: z.boolean().nullish(),
  redirectUrl: z.string().optional().nullable(),
  referrerCode: z.string().optional().nullable(),
  sacCustomPoolSourceCode: z.string().optional().nullable(),
  sourceCode: z.string(),
  successCriteria: SuccessCriteria.optional().nullable(),
  suppressAddonsUpsells: z.boolean().optional().nullable(),
  upsellScenario: z.string().optional().nullable(),
  vanityUrl: z.string().optional().nullable(),
  addonOverride: AddonOverride.optional().nullable(),
  upsellOverride: UpsellOverride.optional().nullable(),
  restricted: z.boolean().optional().nullable(),
});
export type Campaign = z.infer<typeof Campaign>;

export const CampaignResponseFullDTO = Campaign;
export type CampaignResponseFullDTO = z.infer<typeof CampaignResponseFullDTO>;

export const CampaignDbRecord = CampaignResponseFullDTO.partial()
  .and(DynamoDBPrimaryKey)
  .and(DynamoDBSecondaryKey)
  .and(
    z
      .object({
        partnerEnabled: z.boolean().optional().nullable(),
        partnerSlug: z.string().optional().nullable(),
        partnerName: z.string().optional().nullable(),
        partnerLogo: z.string().optional().nullable(),
        partnerBackgroundImage: z.string().optional().nullable(),
        partnerSacFilter: z.string().optional().nullable(),
        partnerSacFilterContentfulEntryId: z.string().optional().nullable(),
        partnerSacFilterContentfulEntryTitle: z.string().optional().nullable(),
        partnerSacFilterTarget: z.string().optional().nullable(),
        partnerBodyContentRef: z.string().optional().nullable(),
      })
      .partial(),
  );
export type CampaignDbRecord = z.infer<typeof CampaignDbRecord>;

const CampaignPayloadBase = Campaign.omit({
  campaignName: true,
  checkoutUrl: true,
  sacCustomPoolSourceCode: true,
  upsellScenario: true,
});

// CampaignPayload is the incoming campaign from legacy
export const CampaignPayload = CampaignPayloadBase.extend({
  name: z.string(),
  devActIdFilter: z.string(),
});
export type CampaignPayload = z.infer<typeof CampaignPayload>;

export const CreateCampaignInput = CampaignResponseFullDTO.partial({
  id: true,
});
export type CreateCampaignInput = z.infer<typeof CreateCampaignInput>;

export const UpdateCampaignInput = CampaignResponseFullDTO.partial().required({
  id: true,
});
export type UpdateCampaignInput = z.infer<typeof UpdateCampaignInput>;

export const DeleteCampaignInput = z.object({
  id: z.string(),
});
export type DeleteCampaignInput = z.infer<typeof DeleteCampaignInput>;

export const ListCampaignsInput = z
  .object({
    limit: z
      .number()
      .int()
      .optional()
      .default(20)
      .describe('Number of results per page.'),
    position: z
      .string()
      .optional()
      .describe(
        "The position to start querying at (returned from last page's query).",
      ),
    sortAsc: z
      .boolean()
      .optional()
      .describe('Pass if you want to sort the results ascending.'),
    sortColumn: SortColumnEnum.describe(
      'Pass which column to sort by. Also the column that powers the "beginsWith" query',
    )
      .optional()
      .default(SortColumnEnum.enum.campaignName),
    beginsWith: z
      .string()
      .optional()
      .describe(
        'The term that is used on a "beginsWith" query on the value in the sortColumn.',
      ),
    searchTerm: z
      .string()
      .optional()
      .describe(
        'The term that is used in a "contains" filter expression on the campaignDisplayName, referrerCode, and vanityUrl fields.',
      ),
    restricted: z
      .boolean()
      .optional()
      .describe('When provided, filters campaigns by their restricted status.'),
  })
  .describe('DTO for retrieving a paginated list.');
export type ListCampaignsInput = z.infer<typeof ListCampaignsInput>;

export const CampaignListDTO = z.object({
  items: CampaignResponseFullDTO.array(),
  position: z
    .string()
    .optional()
    .nullable()
    .describe('Pass this position to the query for the next page of results.'),
  isEnd: z
    .boolean()
    .optional()
    .nullable()
    .describe('There are no more results to page through.'),
});
export type CampaignListDTO = z.infer<typeof CampaignListDTO>;

export const QueryResponseCampaign = z.object({
  items: CampaignDbRecord.array(),
  position: z
    .string()
    .optional()
    .describe('Pass this position to the query for the next page of results.'),
  isEnd: z
    .boolean()
    .optional()
    .nullable()
    .describe('There are no more results to page through.'),
});
export type QueryResponseCampaign = z.infer<typeof QueryResponseCampaign>;

export const QueryResponseCampaignOutput = z.object({
  items: CampaignResponseFullDTO.array(),
  position: z
    .string()
    .optional()
    .describe('Pass this position to the query for the next page of results.'),
  isEnd: z
    .boolean()
    .optional()
    .nullable()
    .describe('There are no more results to page through.'),
});
export type QueryResponseCampaignOutput = z.infer<
  typeof QueryResponseCampaignOutput
>;

export const validateCampaignSchema = (
  campaignData: Campaign | CampaignPayload,
  safeParse = false,
): {
  data?: Campaign;
  error?: ZodError;
  success: boolean;
} => {
  if (safeParse) {
    const CampaignStrict = Campaign.strict();
    return CampaignStrict.safeParse(campaignData);
  }

  return { success: true, data: Campaign.parse(campaignData) };
};

export const validateSuccessCriteriaSchema = (
  successCriteriaData: SuccessCriteria,
) => {
  return SuccessCriteria.safeParse(successCriteriaData);
};
